import { Bell, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useNotifications } from '@/hooks/useNotifications';
import { NotificationList } from './NotificationList';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

export function NotificationBell() {
  const { unreadCount, notifications, isLoading, markAllRead, hasMore } = useNotifications();
  const queryClient = useQueryClient();

  const handleRefresh = () => {
    // Use refetchQueries instead of invalidateQueries to force immediate refetch.
    // invalidateQueries respects staleTime and won't refetch if data is <60s old.
    queryClient.refetchQueries({ queryKey: ['notifications'] });
    queryClient.refetchQueries({ queryKey: ['notifications', 'unread-count'] });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="flex items-center gap-2">
            <span className="font-semibold">Notifications</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              className="h-6 w-6"
              aria-label="Refresh notifications"
            >
              <RefreshCw className="h-3 w-3" />
            </Button>
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAllRead()}
              className="text-xs h-7"
            >
              Mark all read
            </Button>
          )}
        </div>

        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-muted-foreground">
              Loading...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              No notifications
            </div>
          ) : (
            <NotificationList notifications={notifications} />
          )}
        </div>

        {hasMore && (
          <div className="border-t px-3 py-2 text-center">
            <Link
              to="/notifications"
              className="text-sm text-primary hover:underline"
            >
              View all notifications
            </Link>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
